import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Moment from 'moment';
import KEYS from '@/values/keys';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength, email as emailVal } from 'vuelidate/lib/validators';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { SubstituteTeacherService } from '@/tools/services/substitute-teacher.service';
import { SUBSTITUTE_TEACHER_STORE } from '@/tools/store/substitute-teacher.store';

@Component({
  name: 'ed-invite-substitute-teacher'
})
export default class EdInviteSubstituteTeacherComponent extends Vue {

  /* Props */
  @Prop() readonly orgId: number;
  @Prop() readonly invitationData: any;

  /* Data */
  public form: any = {}
  public isSaving: boolean = false
  public classes: any = []
  public classesIds: any = []

  /* Getters */
  get required() {
    return [v => !!v || this.$t('errors.required')]
  }

  get edition() {
    return this.invitationData?.substituteTeacherId ? true : false;
  }

  get minDate () {
    return Moment().format('YYYY-MM-DD')
  }

  get minToDate() {
    return Moment(this.form.startDate.value).format('YYYY-MM-DD');
  }

  get title() {
    return this.edition ? this.$t('tools.substituteTeacher.updateInvitation') : this.$t('tools.staff.info.addStaffDisclamer') 
  }

  /* Methods */
  private init(substituteEmail = '', canEdit = false, canGrade = true, startDate = '', endDate = '', classesIds = []) {
    this.form = EdFormModel([
      EdInputModel('substituteEmail', substituteEmail, {required, emailVal, maxLength: maxLength(100)}),
      EdInputModel('canEdit', canEdit),
      EdInputModel('canGrade', canGrade),
      EdInputModel('startDate', startDate, {required}),
      EdInputModel('endDate', endDate, {required})
    ]);
    this.classesIds = classesIds;
  }

  public cancel() {
    this.$edEventBus.$emit('closeInviteTeacher');
    this.init();
  }
  
  public inviteTeacher() {
    this.isSaving = true;
    let formData = {
      'substituteEmail': this.form.substituteEmail.value,
      'canEdit': this.form.canEdit.value,
      'canGrade': true, //this.form.canGrade.value,
      'startDate': this.form.startDate.value,
      'endDate': this.form.endDate.value,
      'organizationId': this.orgId,
      'classesIds': this.classesIds
    }
    SubstituteTeacherService.createSubstituteTeacher(formData)
    .then(() => {
      this.isSaving = false;
      this.$edEventBus.$emit('closeInviteTeacher');
      this.init();
      this.$store.commit(SUBSTITUTE_TEACHER_STORE.MUTATIONS.RESET_TEACHER_INVITATIONS_M);
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.success.teacherInvited')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch((err) => {
      this.isSaving = false;
      if(err.errors && err.errors[0].status) {
        switch(err.errors[0].status) {
          case 'ST002':
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.overlapInvitation'),
              title: this.$i18n.t('tools.substituteTeacher.info.overlapInvitationTitle'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
          case 'ST003':
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.sameEmail'),
              title: this.$i18n.t('tools.substituteTeacher.info.forbiddenAction'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
          case 'ST004':
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.belongsOtherInstitution'),
              title: this.$i18n.t('tools.substituteTeacher.info.forbiddenAction'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
          case 'ST005': {
            let classes = err.errors[0].attributes.classes.map(el => el.className).join(', ');
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.hasClassesAssigned', {classes}),
              title: this.$i18n.t('tools.substituteTeacher.info.forbiddenAction'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
          }
          default:
            this.$EdToast.create({ 
              msg:  `${this.$t('tools.substituteTeacher.errors.teacherNoInvited')}`,
              type: TOAST_TYPES.DANGER,
              top: '3rem',
              timeout: 3000
            });
            break;
        } 
      } else {
        this.$EdToast.create({ 
          msg:  `${this.$t('tools.substituteTeacher.errors.teacherNoInvited')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
      }
    })
  }

  updateTeacherInvitation() {
    this.isSaving = true;
    let formData = {
      'substituteEmail': this.form.substituteEmail.value,
      'canEdit': this.form.canEdit.value,
      'canGrade': true, // ,this.form.canGrade.value,
      'startDate': this.form.startDate.value,
      'endDate': this.form.endDate.value,
      'organizationId': this.orgId,
      'classesIds': this.classesIds
    }
    SubstituteTeacherService.updateTeacherInvitation(formData, this.invitationData.substituteTeacherId)
    .then(() => {
      this.isSaving = false;
      this.$edEventBus.$emit('closeInviteTeacher');
      this.init();
      this.$store.commit(SUBSTITUTE_TEACHER_STORE.MUTATIONS.RESET_TEACHER_INVITATIONS_M);
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.success.invitationUpdated')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch((err) => {
      this.isSaving = false;
      if(err.errors && err.errors[0].status) {
        switch(err.errors[0].status) {
          case 'ST002':
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.overlapInvitation'),
              title: this.$i18n.t('tools.substituteTeacher.info.overlapInvitationTitle'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
          case 'ST003':
            this.$EdModal.createMessageModal({
              body: this.$i18n.t('tools.substituteTeacher.info.sameEmail'),
              title: this.$i18n.t('tools.substituteTeacher.info.forbiddenAction'),
              type: KEYS.EVENT_TYPES.WARNING
            });
            break;
            case 'ST005': {
              let classes = err.errors[0].attributes.classes.map(el => el.className).join(', ');
              this.$EdModal.createMessageModal({
                body: this.$i18n.t('tools.substituteTeacher.info.hasClassesAssigned', {classes}),
                title: this.$i18n.t('tools.substituteTeacher.info.forbiddenAction'),
                type: KEYS.EVENT_TYPES.WARNING
              });
              break;
            }
          default:
            this.$EdToast.create({ 
              msg:  `${this.$t('tools.substituteTeacher.errors.invitationNoUpdated')}`,
              type: TOAST_TYPES.DANGER,
              top: '3rem',
              timeout: 3000
            });
            break;
        } 
      } else {
        this.$EdToast.create({ 
          msg:  `${this.$t('tools.substituteTeacher.errors.invitationNoUpdated')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
      }
    })
  }

  getAvailableClasses () {    
    SubstituteTeacherService.getAvailableClasses(this.orgId)
    .then((res) => {
      this.classes = res.map(el => {
        return {
          text: el.name,
          value: el.id
        }
      });

      if( this.classes.length == 0 ) {
        throw new Error('No available classes');
      }

    }).catch(() => {
      this.$edEventBus.$emit('closeInviteTeacher');
      this.init();
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.errors.noAvailableClasses')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })
  }

  setInitEdit(data) {
    this.init(data.substituteEmail, data.canEdit, true, data.startDate, data.endDate, data.classNames.map(el => el.id));
  }

  /* Hooks */
  created() {
    this.init();
    this.getAvailableClasses();
    if(this.edition) {
      this.setInitEdit(this.invitationData);
    }
  }

  @Watch('invitationData', {deep: true, immediate: true})
  initEditFrom() {
    if(this.edition && this.invitationData) {
      this.setInitEdit(this.invitationData);
    }
  }

  @Watch('form.startDate.value', {deep: true})
  onStartDateChanged() {
    this.form.setValid('startDate', true);

    if(!this.form.startDate?.value || Moment(this.form.startDate?.value).isAfter(Moment(this.form.endDate?.value))){
      this.form.set('endDate', '')
    }
  }

  @Watch('form.endDate.value', {deep: true})
  onEndDateChanged() {
    if(this.form.endDate?.value){
      this.form.setValid('endDate', true);
    } else {
      this.form.setValid('endDate', false);
    }
  }
}